import type { ImplementationProposal, TChangeImplementationActionType, TChangeImplementationPlanItem } from './types';

export type TImplementationActivityDisplayState = 'READY' | 'IN_PROGRESS' | 'WAITING' | 'COMPLETED' | 'BLOCKED';

export const IMPLEMENTATION_ACTIVITY_DISPLAY_STATE = {
  READY: 'READY',
  IN_PROGRESS: 'IN_PROGRESS',
  WAITING: 'WAITING',
  COMPLETED: 'COMPLETED',
  BLOCKED: 'BLOCKED',
} as const satisfies Record<string, TImplementationActivityDisplayState>;

const ACTION_TYPE_LABELS: Record<TChangeImplementationActionType, string> = {
  MANUAL: 'Manual',
  AI_DOCUMENT_UPDATE: 'AI document update',
  AI_ASSISTED: 'AI-assisted',
  HUMAN_APPROVAL: 'Human approval',
};

const normalizeStatus = (status?: string) => status?.trim().toLocaleLowerCase() ?? '';

const isNotApplicableDependencyValue = (dependency: string) => {
  const normalized = dependency.toLocaleLowerCase();
  return normalized === '-' || normalized === 'none' || normalized === 'n/a';
};

const formatDependencyIdRange = (dependencyIds: string[]) => {
  if (dependencyIds.length < 4) return undefined;

  const parsedIds = dependencyIds.map((id) => {
    const match = id.match(/^([A-Za-z]+)(\d+)$/);
    return match
      ? {
          id,
          prefix: match[1],
          number: Number(match[2]),
          width: match[2].length,
        }
      : undefined;
  });

  if (parsedIds.some((id) => !id)) return undefined;

  const first = parsedIds[0];
  if (!first) return undefined;

  const isConsecutiveRange = parsedIds.every(
    (id, index) => id?.prefix === first.prefix && id.width === first.width && id.number === first.number + index
  );

  return isConsecutiveRange ? `${dependencyIds[0]}\u2013${dependencyIds[dependencyIds.length - 1]}` : undefined;
};

export const getImplementationActivityKey = (item: TChangeImplementationPlanItem, index = 0) =>
  item.id ?? item.activity ?? `${index}`;

export const getImplementationActivityDependencyIds = (item: TChangeImplementationPlanItem) => {
  if (item.dependencies?.length) return item.dependencies;

  const dependency = item.dependency?.trim();
  if (!dependency || isNotApplicableDependencyValue(dependency)) return [];

  return dependency
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean)
    .filter((value) => !isNotApplicableDependencyValue(value));
};

export const isImplementationActivityComplete = (status?: string) =>
  ['complete', 'completed'].includes(normalizeStatus(status));

export const getUnresolvedImplementationDependencyIds = (
  item: TChangeImplementationPlanItem,
  planItems: TChangeImplementationPlanItem[],
  activityStatuses: Record<string, string | undefined> = {}
) =>
  getImplementationActivityDependencyIds(item).filter((dependencyId) => {
    const dependencyIndex = planItems.findIndex((planItem) => planItem.id === dependencyId);
    const dependency = dependencyIndex >= 0 ? planItems[dependencyIndex] : undefined;
    const dependencyStatus = dependency
      ? (activityStatuses[getImplementationActivityKey(dependency, dependencyIndex)] ?? dependency.status)
      : undefined;

    return !isImplementationActivityComplete(dependencyStatus);
  });

export const hasExplicitImplementationBlocker = (
  item: TChangeImplementationPlanItem,
  activityStatuses: Record<string, string | undefined> = {},
  index = 0
) => {
  const status = activityStatuses[getImplementationActivityKey(item, index)] ?? item.status;
  return normalizeStatus(status) === 'blocked' || Boolean(item.blockerReason?.trim());
};

export const getImplementationActivityDisplayState = (
  item: TChangeImplementationPlanItem,
  planItems: TChangeImplementationPlanItem[],
  activityStatuses: Record<string, string | undefined> = {},
  index = 0
): TImplementationActivityDisplayState => {
  const status = activityStatuses[getImplementationActivityKey(item, index)] ?? item.status;
  const normalizedStatus = normalizeStatus(status);

  if (isImplementationActivityComplete(status)) return IMPLEMENTATION_ACTIVITY_DISPLAY_STATE.COMPLETED;
  if (normalizedStatus === 'in progress') return IMPLEMENTATION_ACTIVITY_DISPLAY_STATE.IN_PROGRESS;
  if (hasExplicitImplementationBlocker(item, activityStatuses, index))
    return IMPLEMENTATION_ACTIVITY_DISPLAY_STATE.BLOCKED;
  if (getUnresolvedImplementationDependencyIds(item, planItems, activityStatuses).length > 0)
    return IMPLEMENTATION_ACTIVITY_DISPLAY_STATE.WAITING;

  return IMPLEMENTATION_ACTIVITY_DISPLAY_STATE.READY;
};

export const formatImplementationDependencyIds = (dependencyIds: string[]) => {
  const compactRange = formatDependencyIdRange(dependencyIds);
  if (compactRange) return compactRange;

  if (dependencyIds.length <= 2) return dependencyIds.join(' and ');

  return `${dependencyIds.slice(0, -1).join(', ')} and ${dependencyIds[dependencyIds.length - 1]}`;
};

export const getCompactImplementationDependencyLabel = ({
  dependencyIds,
  unresolvedDependencyIds,
  displayState,
}: {
  dependencyIds: string[];
  unresolvedDependencyIds: string[];
  displayState: TImplementationActivityDisplayState;
}) => {
  if (!dependencyIds.length) return 'No dependencies';

  if (
    (displayState === IMPLEMENTATION_ACTIVITY_DISPLAY_STATE.WAITING ||
      displayState === IMPLEMENTATION_ACTIVITY_DISPLAY_STATE.BLOCKED) &&
    unresolvedDependencyIds.length
  ) {
    return `Waiting for ${formatImplementationDependencyIds(unresolvedDependencyIds)}`;
  }

  return 'Dependencies met';
};

export const getImplementationActionType = (item: TChangeImplementationPlanItem): TChangeImplementationActionType => {
  if (item.actionType) return item.actionType;

  const executionMode = [item.executionMode, item.aiSupportedOrManual].filter(Boolean).join(' ').toLocaleLowerCase();

  if (executionMode.includes('formal human approval')) return 'HUMAN_APPROVAL';
  if (executionMode.includes('tracked changes')) return 'AI_DOCUMENT_UPDATE';
  if (executionMode.includes('ai')) return 'AI_ASSISTED';

  return 'MANUAL';
};

export const getImplementationActionTypeLabel = (item: TChangeImplementationPlanItem) =>
  ACTION_TYPE_LABELS[getImplementationActionType(item)];

export const getImplementationShortDeliverable = (item: TChangeImplementationPlanItem) => {
  if (item.targetDocuments?.length) {
    return item.targetDocuments
      .map((document, index) => {
        if (index === 0 || document.document === document.document.toLocaleUpperCase()) return document.document;

        return `${document.document.charAt(0).toLocaleLowerCase()}${document.document.slice(1)}`;
      })
      .join(', ')
      .replace(/, ([^,]*)$/, ' and $1');
  }

  return item.deliverable ?? item.expectedDeliverable ?? '-';
};

export const isImplementationProposalAvailable = (item: TChangeImplementationPlanItem) =>
  item.implementationProposal?.available === true;

export const canRequestImplementationProposal = (
  item: TChangeImplementationPlanItem,
  displayState: TImplementationActivityDisplayState
) =>
  isImplementationProposalAvailable(item) &&
  Boolean(item.implementationProposal?.triggerLabel) &&
  (displayState === IMPLEMENTATION_ACTIVITY_DISPLAY_STATE.READY ||
    displayState === IMPLEMENTATION_ACTIVITY_DISPLAY_STATE.IN_PROGRESS);

export const getImplementationProposalInitialState = (proposal?: ImplementationProposal) =>
  proposal?.initialState ?? 'NOT_REQUESTED';

export const getImplementationProposalDiffKey = (
  diff: NonNullable<ImplementationProposal['diffs']>[number],
  index = 0
) => diff.id ?? `${diff.document ?? 'diff'}-${index}`;

export const getImplementationProposalRequiredDiffIds = (proposal?: ImplementationProposal) => {
  const diffs = proposal?.diffs ?? [];

  if (proposal?.reviewGate?.requireAllDiffsOpened) {
    return diffs.map(getImplementationProposalDiffKey);
  }

  return [];
};

export const getImplementationProposalReviewGateState = ({
  proposal,
  viewedDiffIds,
  consentChecked,
}: {
  proposal?: ImplementationProposal;
  viewedDiffIds: string[];
  consentChecked: boolean;
}) => {
  const viewedDiffIdSet = new Set(viewedDiffIds);
  const requiredDiffIds = getImplementationProposalRequiredDiffIds(proposal);
  const requiresAnyDiff = Boolean(proposal?.reviewGate?.requireDiffOpened);
  const requiredDiffReviewComplete = requiredDiffIds.length
    ? requiredDiffIds.every((diffId) => viewedDiffIdSet.has(diffId))
    : !requiresAnyDiff || viewedDiffIds.length > 0;
  const consentRequired = Boolean(proposal?.reviewGate?.requireConsent);
  const consentComplete = !consentRequired || consentChecked;

  return {
    requiredDiffIds,
    requiredDiffReviewComplete,
    consentRequired,
    consentComplete,
    canAccept: requiredDiffReviewComplete && consentComplete,
  };
};
